import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import hexToRgbChannels from '../../assets/js/hexToRgbChannels';
import '../../assets/styles/MiniChart.css';


 
const GRID_DIVISIONS = 4;

const MiniChart = React.forwardRef(
  (
    {
      data,
      title,
      unit = '',
      height = 96,
      showGrid = false,
      axisMax,
      axisStep = 1,
      animationDuration = 0.8,
      animationDelayPerBar = 0.07,
      className = '',
      ...props
    },
    ref
  ) => {
    const [hoveredIndex, setHoveredIndex] = React.useState(null);
    const [displayValue, setDisplayValue] = React.useState(null);
    const [isHovering, setIsHovering] = React.useState(false);
    const reduced = useReducedMotion() === true;

    const maxValue = React.useMemo(
      () => Math.max(...data.map((d) => d.value), 0),
      [data]
    );

    const scaleMax = axisMax || maxValue;

    const ticks = React.useMemo(() => {
      if (!axisMax || axisStep <= 0) return null;
      const list = [];
      for (let value = 0; value <= axisMax + 1e-9; value += axisStep) list.push(value);
      return list;
    }, [axisMax, axisStep]);

    const gridStops = ticks
      ? ticks.map((value) => 100 - (value / scaleMax) * 100)
      : Array.from({ length: GRID_DIVISIONS + 1 }).map((_, index) => (100 / GRID_DIVISIONS) * index);

    React.useEffect(() => {
      if (hoveredIndex !== null) setDisplayValue(data[hoveredIndex].value);
    }, [hoveredIndex, data]);

    // Al salir, el numero se va con un poco de retraso para que no parpadee
    // al cruzar de una barra a otra rozando el borde.
    React.useEffect(() => {
      if (isHovering) return undefined;
      const timer = setTimeout(() => setDisplayValue(null), 150);
      return () => clearTimeout(timer);
    }, [isHovering]);

    const handleLeave = () => {
      setIsHovering(false);
      setHoveredIndex(null);
    };

    const columnState = (index) => {
      const isHovered = hoveredIndex === index;
      const isAnyHovered = hoveredIndex !== null;
      const isNeighbor =
        isAnyHovered && (index === hoveredIndex - 1 || index === hoveredIndex + 1);
      return [
        isHovered ? 'is-hovered' : '',
        !isHovered && isNeighbor ? 'is-neighbor' : '',
        !isHovered && !isNeighbor && isAnyHovered ? 'is-dimmed' : ''
      ].filter(Boolean).join(' ');
    };

    return (
      <div
        ref={ref}
        className={`minichart ${isHovering ? 'is-hovering' : ''} ${className}`.trim()}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={handleLeave}
        {...props}
      >
        <div className="minichart__header">
          {title && (
            <span className="minichart__status">
              <span className="minichart__dot" aria-hidden="true" />
              <span className="minichart__title">{title}</span>
            </span>
          )}

          <span
            className={`minichart__readout ${
              isHovering && displayValue !== null ? 'is-active' : ''
            }`}
          >
            {displayValue !== null ? displayValue : ''}
            {unit && (
              <span
                className={`minichart__unit ${displayValue !== null ? 'is-visible' : ''}`}
              >
                {unit}
              </span>
            )}
          </span>
        </div>

        <div className={`minichart__body ${ticks ? 'has-axis' : ''}`.trim()}>
          {ticks && (
            <div className="minichart__axis" style={{ height }} aria-hidden="true">
              {ticks.filter((value) => value > 0).map((value) => (
                <span
                  key={value}
                  className="minichart__tick"
                  style={{ bottom: `${(value / scaleMax) * 100}%` }}
                >
                  {value}
                </span>
              ))}
            </div>
          )}

          <div className="minichart__plot" style={{ height }}>
            {showGrid && (
              <svg className="minichart__canvas" width="100%" height="100%" aria-hidden="true">
                {gridStops.map((stop) => (
                  <line
                    key={stop}
                    className="minichart__gridline"
                    x1="0"
                    x2="100%"
                    y1={`${stop}%`}
                    y2={`${stop}%`}
                  />
                ))}
              </svg>
            )}

            <ol className="minichart__bars">
              {data.map((item, index) => {
                const barHeight = scaleMax === 0 ? 0 : Math.min((item.value / scaleMax) * 100, 100);
                return (
                  <li
                    key={item.label || index}
                    className={`minichart__col ${columnState(index)}`.trim()}
                    onMouseEnter={() => setHoveredIndex(index)}
                  >
                    <span className="minichart__tooltip" aria-hidden="true">
                      {item.label} {item.value}{unit}
                    </span>

                    <motion.span
                      className="minichart__bar"
                      style={{ '--bar-rgb': hexToRgbChannels(item.color) || undefined }}
                      initial={reduced ? false : { height: 0 }}
                      animate={{ height: `${barHeight}%` }}
                      transition={
                        reduced
                          ? { duration: 0 }
                          : {
                              duration: animationDuration,
                              delay: index * animationDelayPerBar,
                              ease: [0.22, 1, 0.36, 1]
                            }
                      }
                    />
                  </li>
                );
              })}
            </ol>
          </div>

          <ol className="minichart__labels">
            {data.map((item, index) => (
              <li
                key={item.label || index}
                className={`minichart__label ${columnState(index)}`.trim()}
                onMouseEnter={() => setHoveredIndex(index)}
              >
                {item.label}
                <span className="tp-sr-only">: {item.value}{unit}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    );
  }
);

MiniChart.displayName = 'MiniChart';

export default MiniChart;
export { MiniChart };
